const SELECTORS = {
  faqItem: "[data-faq-item]",
  faqTrigger: "[data-faq-trigger]",
  header: "[data-site-header]",
  menu: "[data-site-menu]",
  menuToggle: "[data-menu-toggle]",
  reveal: "[data-reveal]",
  year: "[data-current-year]",
};

export function initializeSite({ doc = document, win = window } = {}) {
  const cleanups = [];
  const body = doc.body;
  const header = doc.querySelector(SELECTORS.header);
  const menu = doc.querySelector(SELECTORS.menu);
  const menuToggle = doc.querySelector(SELECTORS.menuToggle);
  const mobileViewport =
    menu && menuToggle ? win.matchMedia?.("(max-width: 860px)") : null;

  const setMenuOpen = (isOpen) => {
    if (!menu || !menuToggle) return;

    const isClosedOnMobile = Boolean(mobileViewport?.matches && !isOpen);
    if (isClosedOnMobile && menu.contains(doc.activeElement)) {
      menuToggle.focus();
    }

    menu.dataset.open = String(isOpen);
    menu.inert = isClosedOnMobile;
    menuToggle.setAttribute("aria-expanded", String(isOpen));
    menuToggle.setAttribute(
      "aria-label",
      isOpen ? "Close navigation menu" : "Open navigation menu",
    );
    body?.classList.toggle("menu-open", isOpen);
  };

  if (menu && menuToggle) {
    setMenuOpen(false);

    const handleMenuToggle = () => {
      setMenuOpen(menuToggle.getAttribute("aria-expanded") !== "true");
    };
    const handleMenuLink = (event) => {
      if (event.target.closest("a")) setMenuOpen(false);
    };
    const handleEscape = (event) => {
      if (
        event.key !== "Escape" ||
        menuToggle.getAttribute("aria-expanded") !== "true"
      )
        return;

      setMenuOpen(false);
      menuToggle.focus();
    };
    const handleViewportChange = () => setMenuOpen(false);

    menuToggle.addEventListener("click", handleMenuToggle);
    menu.addEventListener("click", handleMenuLink);
    doc.addEventListener("keydown", handleEscape);
    mobileViewport?.addEventListener?.("change", handleViewportChange);
    cleanups.push(() =>
      menuToggle.removeEventListener("click", handleMenuToggle),
    );
    cleanups.push(() => menu.removeEventListener("click", handleMenuLink));
    cleanups.push(() => doc.removeEventListener("keydown", handleEscape));
    cleanups.push(() =>
      mobileViewport?.removeEventListener?.("change", handleViewportChange),
    );
  }

  const faqItems = [...doc.querySelectorAll(SELECTORS.faqItem)];
  faqItems.forEach((item) => {
    const trigger = item.querySelector(SELECTORS.faqTrigger);
    const panel = item.querySelector("[data-faq-panel]");
    if (!trigger || !panel) return;

    const handleFaqToggle = () => {
      const willExpand = trigger.getAttribute("aria-expanded") !== "true";

      faqItems.forEach((otherItem) => {
        const otherTrigger = otherItem.querySelector(SELECTORS.faqTrigger);
        const otherPanel = otherItem.querySelector("[data-faq-panel]");
        if (!otherTrigger || !otherPanel) return;
        otherTrigger.setAttribute("aria-expanded", "false");
        otherPanel.hidden = true;
      });

      trigger.setAttribute("aria-expanded", String(willExpand));
      panel.hidden = !willExpand;
    };

    trigger.addEventListener("click", handleFaqToggle);
    cleanups.push(() => trigger.removeEventListener("click", handleFaqToggle));
  });

  doc.querySelectorAll(SELECTORS.year).forEach((element) => {
    element.textContent = String(new Date().getFullYear());
  });

  const updateHeader = () => {
    header?.classList.toggle("is-scrolled", win.scrollY > 24);
  };
  updateHeader();
  win.addEventListener("scroll", updateHeader, { passive: true });
  cleanups.push(() => win.removeEventListener("scroll", updateHeader));

  const revealElements = [...doc.querySelectorAll(SELECTORS.reveal)];
  const reduceMotion = win.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (reduceMotion || !("IntersectionObserver" in win)) {
    revealElements.forEach((element) => {
      element.dataset.visible = "true";
    });
  } else {
    const observer = new win.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.dataset.visible = "true";
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8%", threshold: 0.12 },
    );

    revealElements.forEach((element) => observer.observe(element));
    cleanups.push(() => observer.disconnect());
  }

  return () => cleanups.forEach((cleanup) => cleanup());
}
